import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';

export default function InstallationProcess() {
  const pageData = {
    title: "How Impact Window Installation Works: Step-by-Step",
    metaDescription: "See the full impact window installation timeline in South Florida — from quote to final inspection, including permits, manufacturing, and install day.",
    canonicalPath: "/installation-process",
    
    directAnswer: (
      <>
        <p>
          Impact window and door installation in South Florida follows a structured process from initial quote to final inspection. Most residential projects take <strong>1–3 days for installation</strong>, but the complete timeline from contract signing to final approval is typically <strong>8–12 weeks</strong>, including permit processing, HOA approval (if applicable), and manufacturing lead times.
        </p>
      </>
    ),

    sections: [
      {
        title: "Complete Installation Timeline",
        testId: "installation-timeline",
        table: {
          headers: ["Step", "Timeline", "What Happens"],
          rows: [
            ["1. Get Your Quote", "24–48 hours", "Upload photos → receive Good/Better/Premium pricing options"],
            ["2. Review & Sign Agreement", "1–2 days", "Choose your product tier and sign contract to move forward"],
            ["3. Project Setup", "3–5 days", "Assigned project manager coordinates next steps and schedules field measurement"],
            ["4. Field Measurement", "1 day", "Licensed technician visits to take precise measurements and prepare floor plans"],
            ["5. HOA Approval (if applicable)", "2–4 weeks", "We submit documentation to your HOA or condo board for approval"],
            ["6. Permit Application", "3–7 days", "We prepare and submit permit application to the city building department"],
            ["7. Permit Approval", "3 days – 4 weeks", "City reviews and approves permit (timeline varies by municipality)"],
            ["8. Order Placement", "Same day", "We place your order with the manufacturer after permit approval"],
            ["9. Manufacturing", "4–10 weeks", "Windows and doors are manufactured (timeline varies by product tier)"],
            ["10. Delivery & Scheduling", "3–5 days", "Products arrive; we schedule installation date with you"],
            ["11. Installation Day(s)", "1–3 days", "Licensed crew removes old windows/doors and installs new impact-rated units"],
            ["12. Final Inspection", "1–7 days", "City inspector verifies code compliance and approves installation"],
            ["13. Project Complete", "—", "You receive final documentation for insurance discounts and wind mitigation"]
          ]
        },
        highlight: "Total timeline: 8–12 weeks from contract to completion. HOA approval and manufacturing are the longest phases."
      },

      {
        title: "Installation Day: What to Expect",
        testId: "installation-day",
        content: (
          <p className="mb-4">
            On the day of installation, here's what happens:
          </p>
        ),
        bullets: [
          <><strong>Crew Arrival (7:00–8:00 AM):</strong> Licensed installation team arrives with all materials, tools, and safety equipment</>,
          <><strong>Site Prep (30 minutes):</strong> Interior furniture is moved away from windows, drop cloths are laid down to protect floors</>,
          <><strong>Removal (1–2 hours per opening):</strong> Old windows and doors are carefully removed and debris is cleared</>,
          <><strong>Installation (2–3 hours per opening):</strong> New impact windows/doors are installed, shimmed, leveled, and secured per code</>,
          <><strong>Sealing & Finishing (1 hour per opening):</strong> Exterior and interior caulking, trim work, and final adjustments</>,
          <><strong>Cleanup (30 minutes):</strong> All debris is removed, work area is cleaned, protective films are left on glass</>,
          <><strong>Final Walkthrough:</strong> Project manager reviews installation with you and answers any questions</>
        ],
        highlight: "Most homes (12–15 openings) take 2–3 days. Larger projects may take 3–4 days."
      },

      {
        title: "What You Need to Do",
        testId: "homeowner-responsibilities",
        content: (
          <p className="mb-4">
            We handle almost everything, but here's what we need from you:
          </p>
        ),
        bullets: [
          <><strong>Provide Access:</strong> Ensure crew can access all windows and doors from both inside and outside</>,
          <><strong>Clear Work Areas:</strong> Move furniture, curtains, and decorations at least 3 feet away from windows</>,
          <><strong>Secure Pets:</strong> Keep pets in a separate room or away from the work area for safety</>,
          <><strong>Be Available for Questions:</strong> You don't need to be home the entire time, but we may need quick approvals for minor decisions</>,
          <><strong>Plan for Noise:</strong> Installation involves power tools and some disruption — plan accordingly if working from home</>
        ]
      },

      {
        title: "After Installation: Next Steps",
        testId: "after-installation",
        bullets: [
          <><strong>Inspection Scheduling:</strong> We coordinate with the city to schedule your final inspection (typically within 3–7 days)</>,
          <><strong>Inspector Visit:</strong> City inspector verifies installation meets code and HVHZ requirements (usually takes 30 minutes)</>,
          <><strong>Final Documentation:</strong> You receive permit sign-off, installation certificates, and product warranties</>,
          <><strong>Wind Mitigation:</strong> You can now schedule a wind mitigation inspection to claim insurance discounts (we provide all required documentation)</>,
          <><strong>Protective Film Removal:</strong> Remove protective films from glass after 2–3 weeks (or per manufacturer instructions)</>
        ],
        highlight: "Once the final inspection is passed, your project is officially complete. Insurance discounts can be claimed immediately."
      },

      {
        title: "Common Questions",
        testId: "common-questions",
        content: (
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Do I need to be home during installation?</p>
              <p className="text-[var(--brand-slate-600)]">You don't need to be home the entire time, but someone should be available for the initial walkthrough and final review. Many homeowners leave during the noisiest phases.</p>
            </div>
            <div>
              <p className="font-semibold text-[var(--brand-slate-900)] mb-2">How much disruption should I expect?</p>
              <p className="text-[var(--brand-slate-600)]">Installation involves power tools, debris, and open windows during the process. We use drop cloths to protect floors and clean up thoroughly each day. Plan for noise and limited privacy during installation hours.</p>
            </div>
            <div>
              <p className="font-semibold text-[var(--brand-slate-900)] mb-2">What if it rains during installation?</p>
              <p className="text-[var(--brand-slate-600)]">We monitor weather forecasts closely. If rain is expected, we'll reschedule or pause work to avoid water intrusion. Your home is never left exposed overnight.</p>
            </div>
            <div>
              <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Can I use the windows immediately after installation?</p>
              <p className="text-[var(--brand-slate-600)]">Yes. Windows and doors are fully functional immediately after installation. However, leave protective films on the glass for 2–3 weeks to prevent scratches during the curing period.</p>
            </div>
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Impact Window Pricing",
        description: "See current pricing for your home",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Permit Process",
        description: "How we handle Miami-Dade permits",
        url: "/impact-window-permits-miami-dade"
      },
      {
        title: "Get Your Quote",
        description: "Upload photos and get pricing",
        url: "/get-quote?src=installation_process"
      },
      {
        title: "See a Real Project",
        description: "Kendall case study",
        url: "/case-study-kendall-full-house"
      }
    ],

    ctaText: "Ready to Get Started?",
    ctaSubtext: "Upload photos and receive Good/Better/Premium pricing within 24 hours",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Installation Process" }
    ]
  };

  return <ContentPageTemplate {...pageData} />;
}
